import { useCallback, useEffect, useRef, useState } from 'react';

export type MediaSetup = {
  inputs: MediaDeviceInfo[];
  outputs: MediaDeviceInfo[];
  cams: MediaDeviceInfo[];
  /** Audio-Zugriff erteilt (mind. ein benanntes Eingabegerät sichtbar). Haupttor fürs Setup. */
  ready: boolean;
  /** Kamera-Zugriff erteilt (mind. eine benannte Kamera sichtbar). */
  camReady: boolean;
  /** Läuft gerade ein Permission-/Enumerations-Versuch. */
  requesting: boolean;
  /** Mikro- + Kamera-Berechtigung erneut anfragen (für einen „Berechtigung anfordern"-Button). */
  request: () => Promise<void>;
};

// Geräte-Setup für die Pre-Join-/Setup-Screens: holt EINMAL die Mikrofon- UND Kamera-Berechtigung,
// DANN enumeriert es die Geräte (mit Labels). Stellt zusätzlich `ready`/`request` bereit, damit die UI
// einen „Berechtigung anfordern…"-Button zeigen kann, falls der Dialog weggeklickt/abgelehnt wurde.
//
// WICHTIG: audio und video werden SEPARAT angefragt. `getUserMedia({audio,video})` ist atomar — fehlt die
// Kamera (z. B. Sprecher mit reinem Loopback-Mic), würde der kombinierte Aufruf rejecten und damit AUCH die
// Audio-Berechtigung verweigern. Getrennt: ein fehlendes/verweigertes Gerät blockiert das andere nicht.
export function useMediaSetup(active: boolean): MediaSetup {
  const [inputs, setInputs] = useState<MediaDeviceInfo[]>([]);
  const [outputs, setOutputs] = useState<MediaDeviceInfo[]>([]);
  const [cams, setCams] = useState<MediaDeviceInfo[]>([]);
  const [requesting, setRequesting] = useState(false);
  const aliveRef = useRef(true);
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);

  const enumerate = useCallback(async () => {
    const ds = await navigator.mediaDevices.enumerateDevices();
    if (!aliveRef.current) return;
    setInputs(ds.filter((d) => d.kind === 'audioinput' && d.deviceId));
    setOutputs(ds.filter((d) => d.kind === 'audiooutput' && d.deviceId));
    setCams(ds.filter((d) => d.kind === 'videoinput' && d.deviceId));
  }, []);

  const request = useCallback(async () => {
    if (!aliveRef.current) return;
    setRequesting(true);
    try {
      try { (await navigator.mediaDevices.getUserMedia({ audio: true })).getTracks().forEach((t) => t.stop()); }
      catch { /* kein Mikro / verweigert — trotzdem weiter */ }
      try { (await navigator.mediaDevices.getUserMedia({ video: true })).getTracks().forEach((t) => t.stop()); }
      catch { /* keine Kamera / verweigert — audio-only ist ok */ }
      await enumerate().catch(() => {});
    } finally {
      if (aliveRef.current) setRequesting(false);
    }
  }, [enumerate]);

  useEffect(() => {
    if (!active) return;
    void request();
    navigator.mediaDevices.addEventListener('devicechange', enumerate);
    return () => navigator.mediaDevices.removeEventListener('devicechange', enumerate);
  }, [active, request, enumerate]);

  return { inputs, outputs, cams, ready: inputs.length > 0, camReady: cams.length > 0, requesting, request };
}
